// src/services/vcard/initialization.ts

import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../lib/firebase";
import type { VCard, VCardCategory, VCardCreate } from "../../types/vcard";
import type { UserProfile } from "../../types/user";
import { UniversalDirectoryManager } from "../storage/universalDirectoryManager";

export class VCardInitializationService {
  private static readonly VCARD_VERSION = "4.0";
  private static readonly DEFAULT_ADDRESS = {
    street: "",
    city: "",
    state: "",
    zip: "",
    country: "",
  } as const;

  public static getRequiredCategories(): Record<string, string[]> {
    // Get categories directly from UniversalDirectoryManager's structure
    const categories =
      UniversalDirectoryManager.STRUCTURE.categoryFolders.vcards;

    // Filter out snowGear subcategories and other main categories
    const mainCategories = categories.filter((cat) => !cat.includes("_"));
    const snowGearSubCategories = categories
      .filter((cat) => cat.startsWith("snowGear_"))
      .map((cat) => cat.split("snowGear_")[1]);

    // Create the categories structure with snowGear subcategories
    return {
      ...mainCategories.reduce((acc, cat) => ({ ...acc, [cat]: [] }), {}),
      snowGear: snowGearSubCategories,
    };
  }

  // Method to validate if all required VCards exist
  static async validateInitialization(userId: string): Promise<{
    isInitialized: boolean;
    missingVCards: string[];
  }> {
    const missingVCards: string[] = [];
    const requiredCategories =
      UniversalDirectoryManager.STRUCTURE.categoryFolders.vcards;

    console.log(`Validating VCard initialization for user: ${userId}`);

    // Check all categories from UniversalDirectoryManager
    for (const category of requiredCategories) {
      const vcardPath = UniversalDirectoryManager.getPath({
        userId,
        contentType: "vcard",
        category,
      });

      console.log(`Checking VCard at path: ${vcardPath}`);

      const vcardRef = doc(db, vcardPath);
      const vcardDoc = await getDoc(vcardRef);

      if (!vcardDoc.exists()) {
        console.warn(`Missing VCard: ${category}`);
        missingVCards.push(category);
      } else {
        console.log(`VCard exists: ${category}`);
      }
    }

    const isInitialized = missingVCards.length === 0;
    return { isInitialized, missingVCards };
  }

  // Centralized createVCard method with nested structure for extremeKit
  public static async createVCard(
    userId: string,
    category: VCardCategory,
    userProfile: UserProfile,
  ): Promise<VCard> {
    try {
      const vcardPath = UniversalDirectoryManager.getPath({
        userId,
        contentType: "vcard",
        category,
      });

      const vcardRef = doc(db, vcardPath);
      const firstName = userProfile.firstName || "";
      const lastName = userProfile.lastName || "";
      const formattedName = `${firstName} ${lastName}`.trim();

      let vcard: VCardCreate;

      if (category === "extremeKit") {
        // For extremeKit, use the nested structure
        vcard = {
          id: category,
          userId,
          category,
          version: this.VCARD_VERSION,
          isActive: false,
          firstName,
          lastName,
          formattedName,
          email: userProfile.email || "",
          phone: userProfile.phone || "",
          address: userProfile.address || this.DEFAULT_ADDRESS,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastModified: serverTimestamp(),
          categorySpecificInfo: {
            extremeKit: {
              emergencyContact: {
                name: "",
                phone: "",
                relationship: "",
                phoneCountryCode: "",
              },
              medicalInfo: {
                bloodType: "",
                allergies: "",
                conditions: "",
                medications: "",
                healthFund: "",
                memberNumber: "",
                healthFundId: "",
                cardNumber: "",
                medicareNumber: "",
                medicareIrn: "",
                medicareExpiry: "",
                concessionNumberExpiry: "",
                ndisNumber: "",
              },
              equipment: [],
            },
          },
        };
      } else {
        // For all other categories, use the flat structure
        vcard = {
          id: category,
          userId,
          category,
          version: this.VCARD_VERSION,
          isActive: category === "home",
          firstName,
          lastName,
          formattedName,
          email: userProfile.email || "",
          phone: userProfile.phone || "",
          address: userProfile.address || this.DEFAULT_ADDRESS,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          lastModified: serverTimestamp(),
        };
      }

      // Store in Firestore
      await setDoc(vcardRef, vcard);

      const vcardDoc = await getDoc(vcardRef);
      if (!vcardDoc.exists()) {
        throw new Error("Failed to create VCard in storage.");
      }

      return { ...vcardDoc.data(), id: vcardDoc.id } as VCard;
    } catch (error) {
      console.error("Failed to create VCard:", error);
      throw error;
    }
  }

  // Method to initialize all VCards for a user
  static async initializeUserVCards(
    userId: string,
    userProfile: UserProfile,
  ): Promise<VCard[]> {
    const initializedVCards: VCard[] = [];
    const requiredCategories = UniversalDirectoryManager.STRUCTURE
      .categoryFolders.vcards as VCardCategory[];

    try {
      for (const category of requiredCategories) {
        const vcardPath = UniversalDirectoryManager.getPath({
          userId,
          contentType: "vcard",
          category,
        });

        const vcardRef = doc(db, vcardPath);
        const vcardDoc = await getDoc(vcardRef);

        if (!vcardDoc.exists()) {
          const vcard = await this.createVCard(userId, category, userProfile);
          initializedVCards.push(vcard);
        }
      }

      return initializedVCards;
    } catch (error) {
      console.error("Failed to initialize VCards:", error);
      throw error;
    }
  }
}
